import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Chip, IconButton, Menu, Tooltip } from "@mui/material";
import {
  FLAGGED_REVIEW_REASON,
  ReviewReasonFilter,
  ReviewReasonFilterValue,
  UNFLAGGED_REVIEW_REASON,
} from "components/manualReview/filters/eventFilters";
import { filterPillSx } from "components/manualReview/filters/filterPillSx";
import {
  formatReviewReason,
  TARGETED_REVIEW_REASONS,
} from "components/manualReview/utils/reviewReasons";
import { useTenantTranslation } from "hooks/formatters";

const chipGridSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  p: 1,
  maxWidth: 360,
} as const;

export const ReviewReasonFilterPill = ({
  filter,
  onChange,
  onRemove,
}: {
  filter: ReviewReasonFilter;
  onChange: (filter: ReviewReasonFilter) => void;
  onRemove: () => void;
}) => {
  const { t } = useTenantTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const selectReason = (reason: ReviewReasonFilterValue | null) => {
    onChange({ reason });
    setOpen(false);
  };
  const reasonLabel = (reason: ReviewReasonFilterValue | null): string =>
    reason === null
      ? t("anyReviewReason")
      : reason === FLAGGED_REVIEW_REASON
        ? t("flagged")
        : reason === UNFLAGGED_REVIEW_REASON
          ? t("unflaggedReviewReason")
          : formatReviewReason(reason);

  return (
    <Box sx={filterPillSx}>
      <Box ref={anchorRef} sx={{ display: "inline-flex" }}>
        <Chip
          label={reasonLabel(filter.reason)}
          variant={filter.reason !== null ? "filled" : "outlined"}
          onClick={() => {
            setOpen(true);
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box sx={chipGridSx}>
          <Chip
            label={t("anyReviewReason")}
            variant={filter.reason === null ? "filled" : "outlined"}
            onClick={() => {
              selectReason(null);
            }}
          />
          <Chip
            label={t("flagged")}
            variant={
              filter.reason === FLAGGED_REVIEW_REASON ? "filled" : "outlined"
            }
            onClick={() => {
              selectReason(FLAGGED_REVIEW_REASON);
            }}
          />
          <Chip
            label={t("unflaggedReviewReason")}
            variant={
              filter.reason === UNFLAGGED_REVIEW_REASON ? "filled" : "outlined"
            }
            onClick={() => {
              selectReason(UNFLAGGED_REVIEW_REASON);
            }}
          />
          {TARGETED_REVIEW_REASONS.map((reason) => (
            <Chip
              key={reason}
              label={formatReviewReason(reason)}
              variant={filter.reason === reason ? "filled" : "outlined"}
              onClick={() => {
                selectReason(reason);
              }}
            />
          ))}
        </Box>
      </Menu>
      {filter.reason === "unplanned_material" ? (
        <Tooltip title={t("unplannedMaterialNotInCalendarCount")}>
          <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </Tooltip>
      ) : null}
      {filter.reason === UNFLAGGED_REVIEW_REASON ? (
        <Tooltip title={t("unflaggedNotInCalendarCount")}>
          <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </Tooltip>
      ) : null}
      <IconButton
        size="small"
        aria-label={t("removeFilter")}
        onClick={onRemove}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
